import type { ModalBodyController, ModalBodyProps } from '@ui/core/modal/body/ModalBody';
import { modalState } from '@ui/core/modal/renderer/state';
import type { Whatever } from '@utils/types';
import type { Ref } from 'preact/hooks';
import { useImperativeHandle, useRef } from 'preact/hooks';

export type ModalControllerProps = { open: () => void; close: () => void };

type ModalPropsController = { controller?: Ref<ModalControllerProps> };
type ModalPropsControlled = { isOpen: boolean };

interface ModalProps extends Omit<ModalBodyProps, 'id' | 'controller'> {
    onClose?: () => void;
}

function Modal(props: ModalPropsControlled & ModalProps): null;
function Modal(props: ModalPropsController & ModalProps): null;
function Modal(props: (ModalPropsControlled | ModalPropsController) & ModalProps) {
    const id = useRef<number>();
    // either isOpen or controller is present, one is always missing
    const { isOpen, controller, ...rest } = props as ModalPropsControlled & ModalPropsController & ModalProps;
    const modalController = useRef<ModalBodyController>(null);

    function onClose() {
        id.current = undefined;
        props.onClose?.();
    }

    function open() {
        if (id.current) return;
        id.current = Modal.open({ ...props, controller: modalController, onClose } as ModalBodyProps).item.id;
    }

    function close() {
        if (id.current) {
            Modal.close(id.current);
            id.current = undefined;
        }
    }

    useImperativeHandle(controller || null, () => ({
        open,
        close,
    }));

    if (id.current) modalState.update({ id: id.current, ...rest, controller: modalController, onClose });
    if (id.current && isOpen === false) close();
    if (!id.current && isOpen) open();

    return null;
}

Modal.open = (modal: Omit<ModalBodyProps, 'id' | 'controller'> | ((id: number) => Omit<ModalBodyProps, 'id' | 'controller'>)) => {
    return modalState.add((id) => {
        const props = typeof modal === 'function' ? modal(id) : modal;
        (props as Whatever).controller = { current: null };
        const backdrop = document.getElementById('modal-backdrop');
        backdrop?.classList.remove('closing');
        return props as ModalBodyProps;
    });
};

Modal.close = (id?: number) => {
    return modalState.get(id ?? modalState.items[modalState.items.length - 1].id)?.controller.current?.close();
};

Modal.closeAll = () => {
    return modalState.items.forEach(({ controller }) => controller.current?.close());
};

export { Modal };
